/* La facturation des commissions.
 *
 * Le circuit, dans l'ordre : l'usine a payé la vente, Omnix lui facture la
 * commission totale, l'agent refacture sa part à Omnix, et la différence
 * reste à Omnix. Une commission facturée est figée : son montant ne suit
 * plus le barème, même si celui-ci change ensuite. */

import { supabase } from "@/lib/supabase";
import { ecrireParametre, lireParametre } from "@/lib/donnees";
import { aujourdhui } from "@/lib/format";
import type { Commission } from "@/types/metier";

export interface SerieFacture {
  serie: string;
  prochain: number;
}

const SERIE_DEFAUT: SerieFacture = { serie: "FAC/2026/", prochain: 1001 };

export async function lireSerie(): Promise<SerieFacture> {
  const s = await lireParametre<SerieFacture>("facturation", SERIE_DEFAUT);
  return { serie: s?.serie ?? SERIE_DEFAUT.serie, prochain: Number(s?.prochain ?? SERIE_DEFAUT.prochain) };
}

export function numeroSuivant(s: SerieFacture): string {
  return `${s.serie}${s.prochain}`;
}

/**
 * Émet la facture Omnix d'une commission : elle prend son numéro dans la
 * série, passe en « facturée », et la série avance d'un cran.
 */
export async function emettreFacture(c: Commission): Promise<string> {
  const s = await lireSerie();
  const numero = numeroSuivant(s);
  const { error } = await supabase
    .from("commissions")
    .update({ etat: "facturee", fac_omnix: { numero, date: aujourdhui() } })
    .eq("id", c.id);
  if (error) throw new Error(error.message);
  await ecrireParametre("facturation", { ...s, prochain: s.prochain + 1 });
  return numero;
}

/** Émet les factures une par une : la série doit rester continue. */
export async function emettreToutes(liste: readonly Commission[]): Promise<number> {
  let n = 0;
  for (const c of liste) {
    await emettreFacture(c);
    n += 1;
  }
  return n;
}

/** L'usine a réglé la facture de commission. */
export async function marquerReglee(c: Commission): Promise<void> {
  const { error } = await supabase.from("commissions").update({ etat: "payee" }).eq("id", c.id);
  if (error) throw new Error(error.message);
}

/** L'agent a envoyé sa facture à Omnix pour sa part. */
export async function recevoirFactureAgent(c: Commission, numero?: string): Promise<void> {
  const { error } = await supabase
    .from("commissions")
    .update({ fac_agent: { numero: numero?.trim() || null, date: aujourdhui() } })
    .eq("id", c.id);
  if (error) throw new Error(error.message);
}
